/* eslint-disable react/react-in-jsx-scope */

import {
  TemplateTaskUrlFragmentFragment,
  TemplateUrlTypes,
} from '@/gql/graphql';
import { useModals } from '@/hooks/ModalManager';
import { CopyIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Button,
  Center,
  chakra,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Link,
  List,
  ListItem,
  Spinner,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import {
  getFragmentData,
  templateTaskUrlEmailFragment,
  templateTaskUrlFragment,
  useCreateTemplateTaskUrl,
  useDeleteTemplateTaskUrl,
  useTemplateTaskUrlsQuery,
  useToggleUrlActive,
} from '../hooks/useShareTemplateTask';
import { getShareableLink } from '../utils';
import { useShareViewModal } from './ShareViewModal';

const gridTemplateColumns = '0.9fr 0.5fr 0.6fr 0.7fr 0.3fr 0.6fr';

const useUrlTypes = () => {
  const { t } = useTranslation();
  return {
    [TemplateUrlTypes.OnlyUse]: t('registrations.single_use'),
    [TemplateUrlTypes.Recurrency]: t('registrations.recurrent'),
  };
};

function ShareItem({
  item,
  onRefetch,
}: {
  item: TemplateTaskUrlFragmentFragment;
  onRefetch: () => void;
}) {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();
  const view = useShareViewModal();
  const urlTypes = useUrlTypes();
  const [onDelete, { loading: deleting }] = useDeleteTemplateTaskUrl();

  const link = getShareableLink(item.id);

  const [isActive, setActive] = React.useState(!!item.active);

  const [update, payload] = useToggleUrlActive();

  const handleUpdate = React.useCallback(() => {
    const nextState = !isActive;

    setActive(nextState);

    update({
      id: item.id,
      active: nextState,
    });
  }, [isActive, item.id, update]);

  return (
    <ListItem borderBottomWidth="thin" py={3}>
      <Grid
        gridTemplateColumns={gridTemplateColumns}
        w="full"
        gap="1"
        color="blackAlpha.700"
        fontWeight="bold"
      >
        <GridItem alignItems="center" display="flex" pl="2">
          <Link w="full" href={link.href}>
            <Text noOfLines={2} wordBreak="break-all">{link.href}</Text>
          </Link>
        </GridItem>
        <GridItem
          justifyContent="center"
          alignItems="center"
          display="flex"
          textAlign="center"
        >
          {!!item.urlType && <span>{urlTypes[item.urlType]}</span>}
        </GridItem>
        <GridItem alignItems="center" display="flex" justifyContent="center">
          <Text textAlign="center">{moment(new Date(item.updatedAt)).format('LLL ')}</Text>
        </GridItem>
        <GridItem alignItems="center" display="flex" justifyContent="center">
          <ul>
            {item.templateTaskUrlEmails
              ?.slice(0, 3)
              ?.map((email) => {
                const frag = getFragmentData(
                  templateTaskUrlEmailFragment,
                  email,
                );
                return frag.email;
              })
              .map((e) => (
                <li key={e}>
                  <Text fontSize="sm" noOfLines={1} wordBreak="break-all">
                    {e}
                  </Text>
                </li>
              ))}
          </ul>
        </GridItem>
        <GridItem alignItems="center" display="flex" justifyContent="center">
          <Switch
            disabled={payload.loading}
            isChecked={isActive}
            onChange={handleUpdate}
          />
        </GridItem>
        <GridItem
          alignItems="center"
          display="flex"
          justifyContent="flex-end"
          gap={2}
        >
          <Tooltip label={t('registrations.copy_link')}>
            <IconButton
              onClick={() => {
                copyToClipboard(link.href);
              }}
              icon={<CopyIcon />}
              aria-label="copy"
            />
          </Tooltip>
          <Button onClick={() => view(item.id)}>{t('common.edit')}</Button>
          <Tooltip label={t('common.delete')}>
            <IconButton
              isLoading={deleting}
              onClick={() => onDelete(item.id, onRefetch)}
              icon={<DeleteIcon />}
              aria-label="delete"
            />
          </Tooltip>
        </GridItem>
      </Grid>
    </ListItem>
  );
}

function ShareListHeader() {
  const { t } = useTranslation();
  return (
    <Grid
      gridTemplateColumns={gridTemplateColumns}
      position="sticky"
      zIndex="docked"
      top={0}
      w="full"
      h="10"
      bg="white"
      gap="1"
      color="blackAlpha.700"
      fontWeight="bold"
      borderBottomWidth="thin"
    >
      <GridItem display="flex" alignItems="center" justifyContent="center">
        {t('registrations.link')}
      </GridItem>
      <GridItem display="flex" alignItems="center" justifyContent="center">
        <span>{t('registrations.type')}</span>
      </GridItem>
      <GridItem display="flex" alignItems="center" justifyContent="center">
        {t('registrations.last_edit')}
      </GridItem>
      <GridItem display="flex" alignItems="center" justifyContent="center">
        {t('registrations.emails')}
      </GridItem>
      <GridItem display="flex" alignItems="center" justifyContent="center">
        <span>{t('common.active')}</span>
      </GridItem>
      <GridItem />
    </Grid>
  );
}

function ShareList({ templateTaskId }: { templateTaskId: string }) {
  const { t } = useTranslation();
  const { data, loading, refetch } = useTemplateTaskUrlsQuery({ templateTaskId });

  if (loading && !data?.templateTaskUrls) {
    return (
      <Center minH="36" bg="gray.100" rounded="md" flex="1">
        <Spinner />
      </Center>
    );
  }

  if (!data?.templateTaskUrls?.length) {
    return (
      <Center minH="36" bg="gray.100" rounded="md" flex="1">
        <Text>{t('registrations.no_shares_yet')}</Text>
      </Center>
    );
  }

  return (
    <List maxH={350} overflowY="auto" spacing={0} w="100%" pr={4}>
      <ShareListHeader />
      {data?.templateTaskUrls.map((item) => (
        <ShareItem key={item.id} item={item} onRefetch={refetch} />
      ))}
    </List>
  );
}

export function ShareListModal({ templateTaskId }: { templateTaskId: string }) {
  const viewShareLink = useShareViewModal();
  const [mutate, { loading }] = useCreateTemplateTaskUrl();

  const handleCreate = React.useCallback(() => {
    mutate({
      variables: {
        input: {
          templateTaskId,
          urlType: TemplateUrlTypes.Recurrency,
          active: true,
        },
      },
      onCompleted(data) {
        if (!data.createTemplateTaskUrl?.templateTaskUrl) return;
        const fragment = getFragmentData(
          templateTaskUrlFragment,
          data.createTemplateTaskUrl?.templateTaskUrl,
        );
        viewShareLink(fragment.id);
      },
    });
  }, [mutate, templateTaskId, viewShareLink]);
  const { t } = useTranslation();
  return (
    <Flex flex={1} px={6} pb={4} flexDir="column" gap="4">
      <chakra.p fontSize="lg">
        {t('registrations.create_public_links_description')}
      </chakra.p>
      <Flex gap={4} flexDir="column" flex="1">
        <ShareList templateTaskId={templateTaskId} />
        <Button
          disabled={loading}
          isLoading={loading}
          onClick={handleCreate}
          colorScheme="brand"
        >
          {t('registrations.new_share_link')}
        </Button>
      </Flex>
    </Flex>
  );
}

export function useShareListModal() {
  const modals = useModals();
  const { t } = useTranslation();

  return (props: { templateTaskId: string }) => {
    const id = 'share_list_modal';
    modals.open({
      id,
      title: t('registrations.sharing'),
      closeOnEsc: false,
      closeOnOverlayClick: false,
      size: '6xl',
      contentMaxW: '1400px',
      body: <ShareListModal {...props} />,
      footer: (
        <Button onClick={() => modals.pop()} size="lg" variant="outline">
          {t('registrations.back')}
        </Button>
      ),
    });
  };
}
